import ChevronUpIcon from '@atlaskit/icon/glyph/hipchat/chevron-up';
import ChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import EmojiFrequentIcon from '@atlaskit/icon/glyph/emoji/frequent';
import { colors } from '@atlaskit/theme';

import React from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import config from 'appconfig';

import { Dimensions } from 'components/visuals';
import { LICENSE_EVALUATION } from 'model/hams/LicenseType';
import { isInNext30Days, isPastEndDate } from 'util/mac/helpers';
import { removeTimezone } from '../common/utils';

const Cell = styled.td<{ width: number; $textColor?: string }>`
  font-size: 14px;
  align-self: center;
  width: ${({ width }) => width}%;
  padding: ${Dimensions.units(1)}px ${Dimensions.units(2)}px ${Dimensions.units(1)}px 0;
  overflow-wrap: anywhere;
  color: ${(props) => (props.$textColor ? props.$textColor : 'unset')};

  && span {
    line-height: unset;
  }
`;

const ChevronCellWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const ExpiredDateWrapper = styled.span`
  color: ${colors.R500};
`;

export const SoonExpiredDateWrapper = styled.span`
  color: ${colors.G400};
`;

const ExpiredWrapper = styled.div`
  width: ${Dimensions.units(7)}px;
  color: ${colors.R300};
  border: 1px solid ${colors.N60};
  border-radius: ${Dimensions.units(0.5)}px;
  font-size: 11px;
  font-weight: bold;
  line-height: 99%;
  padding: ${Dimensions.units(0.25)}px ${Dimensions.units(0.5)}px;
  text-align: center;
  text-transform: uppercase;
`;

export const MigratedWrapper = styled(ExpiredWrapper)`
  width: fit-content;
  color: ${colors.B300};
  background-color: ${colors.B50};
  border: none;
  font-size: 14px;
  white-space: nowrap;
`;

const ExpirationDateComponent = ({
  expireDate,
  isExpired,
  expiresIn30Days,
  isPartnerAndMigrated,
}) => {
  const expirationDateMessage = (
    <FormattedDate value={removeTimezone(expireDate)} year="numeric" month="short" day="2-digit" />
  );

  if (isExpired) {
    return (
      <ExpiredDateWrapper data-cy="expiration-date-red">{expirationDateMessage}</ExpiredDateWrapper>
    );
  }

  if (expiresIn30Days) {
    return (
      <SoonExpiredDateWrapper data-cy="expiration-date-green">
        {expirationDateMessage}
      </SoonExpiredDateWrapper>
    );
  }

  if (isPartnerAndMigrated) {
    return (
      <MigratedWrapper>
        <FormattedMessage defaultMessage="Migrated" id="mac.licenses.migrated" />
      </MigratedWrapper>
    );
  }

  return expirationDateMessage;
};

const MaintenanceMessage = ({
  marketplaceAddOn,
  marketplaceSupportLink,
  supportContactLink,
  isExpired,
  isEvaluation,
}) =>
  isExpired ? (
    !isEvaluation && (
      <FormattedMessage
        defaultMessage="Maintenance Expired"
        id="mac.licenses.maintenance-expired"
      />
    )
  ) : (
    <a href={marketplaceAddOn ? marketplaceSupportLink : supportContactLink}>
      <FormattedMessage defaultMessage="Request Support" id="mac.licenses.request-support" />
    </a>
  );

export const LicensesCollapsedRow = ({ showContent, rowItem, isTrial, isPartner }) => {
  const {
    accountId,
    licenseId,
    expireDate,
    productDescription,
    accountName,
    friendlyName,
    licenseType,
    marketplaceAddOn,
    productKey,
    dataCenter,
    entSubMigrated,
  } = rowItem;

  const accountNameMessage = friendlyName || accountName || (
    <FormattedMessage defaultMessage="Not yet named" id="mac.licenses.not-yet-named" />
  );
  const isEvaluation = licenseType === LICENSE_EVALUATION;
  const expirationDate = new Date(expireDate);
  const isExpired = isPastEndDate(expirationDate);
  const productKeyUpdated = productKey?.replace('.data-center', '').replace('pricingplan.', '');
  const hostedType = dataCenter ? 'datacenter' : 'server';
  const marketplaceSupportLink = `${config.marketplaceUrl}/plugins/${productKeyUpdated}/${hostedType}/support`;
  const supportContactLink = `${config.support}/contact`;
  const isPartnerAndMigrated = isPartner && entSubMigrated;

  return (
    <>
      <Cell width={15} $textColor={entSubMigrated ? colors.N60 : 'unset'} key="licenses-row-sen">
        <ChevronCellWrapper>
          {showContent ? <ChevronUpIcon label="" /> : <ChevronDownIcon label="" />}
          {isTrial && <EmojiFrequentIcon label="" />}
          SEN-{accountId || `L${licenseId}`}
        </ChevronCellWrapper>
      </Cell>
      <Cell
        width={25}
        $textColor={entSubMigrated ? colors.N60 : 'unset'}
        key="licenses-row-product"
      >
        {productDescription}
      </Cell>
      <Cell
        width={20}
        $textColor={entSubMigrated ? colors.N60 : 'unset'}
        key="licenses-row-site-name"
        data-cy={`license-collapsed-row-name-${accountId}`}
      >
        {accountNameMessage}
      </Cell>
      <Cell
        width={15}
        key="licenses-row-support-expire"
        data-cy={`license-collapsed-row-expiration-date-${accountId}`}
      >
        {entSubMigrated ? (
          <MigratedWrapper>
            <FormattedMessage defaultMessage="Migrated" id="mac.licenses.migrated" />
          </MigratedWrapper>
        ) : (
          <>
            <ExpirationDateComponent
              expireDate={expireDate}
              isExpired={isExpired}
              expiresIn30Days={isInNext30Days(expirationDate)}
              isPartnerAndMigrated={isPartnerAndMigrated}
            />
            {isExpired && (
              <ExpiredWrapper>
                <FormattedMessage defaultMessage="Expired" id="mac.licenses.expired" />
              </ExpiredWrapper>
            )}
          </>
        )}
      </Cell>
      {!entSubMigrated && (
        <Cell
          width={25}
          key="licenses-row-support"
          data-cy={`license-collapsed-row-support-${accountId}`}
        >
          {!isPartnerAndMigrated && (
            <MaintenanceMessage
              marketplaceAddOn={marketplaceAddOn}
              marketplaceSupportLink={marketplaceSupportLink}
              supportContactLink={supportContactLink}
              isExpired={isExpired}
              isEvaluation={isEvaluation}
            />
          )}
        </Cell>
      )}
    </>
  );
};
